import React, { useRef, useState, useEffect, useCallback } from 'react';
import "./cameraOnBoarding.css";
import QrScanner from "qr-scanner";
import ChaloTopNav from '../../../../shared/widgets/chaloTopNav';
import ChaloAlertDialog from '../../../../shared/widgets/chaloAlertDialog';
import ChaloLoader from '../../../../shared/widgets/chaloLoader';
import { fetchLiveFeedDetails } from '../../../../services/vehicleMasterService';
import { AuthenticationService } from '../../../../services/authorization';
import { useLocation, useNavigate } from 'react-router-dom';

const ScanCameraQr = () => {
    const videoEl = useRef(null);
    const qrBoxEl = useRef(null);
    const scannerRef = useRef(null);
    const location = useLocation();
    const cameraBrand = location?.state?.cameraBrand || null;

    const authService = AuthenticationService.getInstance();
    const parentRoute = authService.getMasterParentRoute();

    const [showModal, setShowModal] = useState(false);
    const [errorHeading, setErrorHeading] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);


    const navigate = useNavigate();
    const isCameraIdValid = (value) =>
      /^[a-zA-Z0-9]{10}$/.test(value.trim());


    const extractCameraId = (qrValue) => {
        if (!qrValue) return null;

        const match = qrValue.match(/[a-zA-Z0-9]{10}$/);
        return match ? match[0].toUpperCase() : null;
    };


    const getFailureReason = (data) => {
        if (data?.description) return { heading: 'Error', message: data.description };
        if (data?.error) return { heading: 'Error', message: data.error };
        return { heading: 'Error', message: 'An unknown error occurred' };
    };

    // Handler for successful QR scan.
    const onScanSuccess = useCallback((result) => {
        setIsLoading(true);
        const cameraId = result.data;

        if (scannerRef.current) {
            scannerRef.current.stop();
        }

        const extractedCameraId = extractCameraId(cameraId);

        if (extractedCameraId && isCameraIdValid(extractedCameraId)) {
            getLiveFeedDetails(extractedCameraId, cameraBrand);
        } else {
            setIsLoading(false);
            setErrorHeading('Wrong QR Code');
            setErrorMessage('Try scanning again to add the camera');
            setShowModal(true);
        }
    }, []);

    const getLiveFeedDetails = async (cameraId,cameraBrand) => {
        try {
            const data = await fetchLiveFeedDetails(cameraId, cameraBrand);
            handleLiveFeedResponse(data, cameraId);
        } catch (error) {
            let failureReason;
            if (error && error.status === 400) {
                const combinedData = { liveFeed: false, cameraId, cameraBrand, parentRoute };
                const path = `${parentRoute}/cameraDetails`;
                navigate(path, { state: combinedData });
                return;
            }

            if (error && typeof error === 'object') {
                failureReason = getFailureReason(error);
            } else {
                failureReason = { heading: 'Network Error', message: 'There was a problem connecting to the API.' };
            }

            setErrorHeading(failureReason.heading);
            setErrorMessage(failureReason.message);
            setShowModal(true);
            setIsLoading(false);

            if (scannerRef.current) {
                scannerRef.current.stop();
            }
        }
    };

    const handleLiveFeedResponse = (data, cameraId) => {
        setIsLoading(false);
        const pathBase = `${parentRoute}`;

        if (data.cameraConnected) {
            const combinedData = { ...data, cameraId, cameraBrand};
            navigate(`${pathBase}/liveFeedPreview`, { state: { feedData: combinedData } });
        } else {
            const combinedData = { liveFeed: false, cameraId, cameraBrand };
            navigate(`${pathBase}/cameraDetails`, { state: combinedData });
        }
    };

    const onScanFail = useCallback((err) => {
        console.log('QR Scan error:', err);
    }, []);

    const closeModal = useCallback(() => {
        setShowModal(false);
        try {
            if (scannerRef.current) {
                scannerRef.current.start().catch((err) => console.error('Error restarting scanner:', err));
            }
        } catch (error) {
            console.error('Error in closeModal:', error);
        }
    }, []);

    useEffect(() => {
        if (!cameraBrand) {
            navigate(`${parentRoute}/addCamera`);
        }
    }, [cameraBrand, navigate, parentRoute]);

    useEffect(() => {
        if (videoEl.current && !scannerRef.current) {
            scannerRef.current = new QrScanner(videoEl.current, onScanSuccess, {
                onDecodeError: onScanFail,
                preferredCamera: 'environment',
                highlightScanRegion: true,
                highlightCodeOutline: true,
                overlay: qrBoxEl.current || undefined,
            });

            scannerRef.current.start().catch((err) => {
                console.error('Error starting QR scanner:', err);
            });
        }

        return () => {
            if (scannerRef.current) {
                scannerRef.current.stop();
            }
        };
    }, [onScanSuccess, onScanFail]);

    return (
        <div className='scanner-container row'>
            <ChaloTopNav navContent="QR Code Scanner" />
            {isLoading && <ChaloLoader />}
            <div className="qr-reader">
                <video ref={videoEl} className="full-screen-video" autoPlay>
                    <track kind="captions" srcLang="en" label="Placeholder" />
                </video>
                <p className='scan-brief'>Align the QR code within the frame to scan</p>
            </div>
            {showModal && (
                <div className="error-dialog-container">
                    <ChaloAlertDialog 
                        heading={errorHeading} 
                        message={errorMessage} 
                        onClose={closeModal} 
                    />
                </div>
            )}
        </div>
    );
};

export default ScanCameraQr;
